import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, UserCircle2 } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useSync } from "@/lib/live";
import { ExportButton } from "@/admin/AdminDashboard";
import { StatusBadge } from "@/components/Status";
import { Thread } from "@/pages/ComplaintDetail";
import { Textarea } from "@/components/ui/textarea";
import { useDetail } from "@/admin/AdminDetail";
import { ContactButtons, Empty } from "@/admin/ui";

const SLA_H = { Urgent: 4, High: 12, Normal: 24, Low: 48 };
const PR_STYLE = { Urgent: "bg-[#E05C3A] text-white", High: "bg-[#FBE3D9] text-[#B4401F]", Normal: "bg-[#EEF0E9] text-[#4A6358]", Low: "bg-[#EEF5FF] text-[#3B6FD8]" };
const FILTERS = [["open", "All open"], ["mine", "Mine"], ["unassigned", "Unassigned"], ["breached", "SLA breached"], ["resolved", "Resolved"]];

export const sla = (c) => {
  if (c.status === "Resolved") return { label: "Resolved", late: false };
  const left = SLA_H[c.priority || "Normal"] - (Date.now() - new Date(c.created_at).getTime()) / 3.6e6;
  const h = Math.abs(left);
  const txt = h < 1 ? `${Math.round(h * 60)} min` : `${Math.round(h)} h`;
  return left < 0 ? { label: `Overdue ${txt}`, late: true } : { label: `${txt} left`, late: false };
};

const Pick = ({ value, options, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 rounded-full border border-[#D6DBD2] bg-white px-3 text-xs font-bold text-[#0D3B2B]">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const Ticket = ({ c, active, onClick, team }) => {
  const s = sla(c);
  return (
    <button onClick={onClick} data-testid={`admin-complaint-${c.complaint_id}`}
      className={`w-full rounded-xl border p-4 text-left transition-colors ${active ? "border-[#0D3B2B] bg-[#F3EDDC]" : "border-[#E3E6DD] bg-white hover:border-[#2E9E6B]"}`}>
      <div className="flex items-center justify-between gap-2"><b className="text-sm text-[#0D3B2B]">{c.complaint_id}</b><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${PR_STYLE[c.priority || "Normal"]}`}>{c.priority || "Normal"}</span></div>
      <p className="mt-1 text-sm">{c.category}{c.booking_id && ` · ${c.booking_id}`}</p>
      <p className="text-xs text-[#6B7F74]">{c.business_name || c.name || c.mobile}</p>
      <div className="mt-2 flex items-center justify-between text-[11px] font-bold">
        <span className={`inline-flex items-center gap-1 ${s.late ? "text-[#E05C3A]" : "text-[#2E9E6B]"}`} data-testid={`sla-${c.complaint_id}`}><Clock className="h-3 w-3" />{s.label}</span>
        <span className="inline-flex items-center gap-1 text-[#6B7F74]"><UserCircle2 className="h-3 w-3" />{team.find((t) => t.email === c.assigned_to)?.name || "Unassigned"}</span>
      </div>
    </button>
  );
};

const Detail = ({ c, team, reload }) => {
  const open = useDetail();
  const [reply, setReply] = useState("");
  const meta = (body, msg) => api.put(`/admin/complaints/${c.complaint_id}/meta`, body).then(() => { toast.success(msg); reload(); }).catch((e) => toast.error(errMsg(e)));
  const send = async (status) => {
    try {
      await api.post(`/admin/complaints/${c.complaint_id}/reply`, { message: status ? "" : reply, status: status || null });
      if (!status) setReply("");
      toast.success(status ? `Status set to ${status}` : "Reply sent");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4 rounded-2xl border border-[#E3E6DD] bg-white p-5" data-testid="admin-complaint-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="ks-serif text-2xl text-[#0D3B2B]">{c.complaint_id} · {c.category}</p>
          <button onClick={() => open({ type: "customer", id: c.user_id, title: c.business_name || c.name || c.mobile })} className="text-sm font-bold text-[#2E9E6B] underline" data-testid="complaint-open-360">{c.business_name || c.name || "Customer"} ({c.customer_type}) · view 360</button></div>
        <ContactButtons mobile={c.mobile} testid="complaint" />
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#F7F5EF] p-3">
        <StatusBadge status={c.status} />
        <Pick value={c.status} onChange={send} options={["Open", "In progress", "Resolved"].map((s) => [s, s])} testid="admin-complaint-status-select" />
        <Pick value={c.priority || "Normal"} onChange={(v) => meta({ priority: v }, `Priority set to ${v}`)} options={Object.keys(SLA_H).map((p) => [p, `${p} · ${SLA_H[p]}h SLA`])} testid="complaint-priority-select" />
        <Pick value={c.assigned_to || ""} onChange={(v) => meta({ assigned_to: v }, v ? "Assigned" : "Unassigned")} options={[["", "Unassigned"], ...team.map((t) => [t.email, `${t.name} (${t.role})`])]} testid="complaint-assign-select" />
        <span className={`ml-auto text-xs font-bold ${sla(c).late ? "text-[#E05C3A]" : "text-[#2E9E6B]"}`}>{sla(c).label}</span>
      </div>
      {c.photo_path && <a href={fileUrl(c.photo_path)} target="_blank" rel="noreferrer"><img src={fileUrl(c.photo_path)} alt="" className="h-32 rounded-xl object-cover" /></a>}
      <Thread messages={c.messages} mine="admin" />
      <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply to the customer…" data-testid="admin-reply-input" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => (reply.trim() ? send() : toast.error("Type a reply first."))} className="h-11 rounded-full bg-[#0D3B2B] px-6 font-bold text-white" data-testid="admin-reply-send-btn">Send reply</button>
        {c.status !== "Resolved" && <button onClick={() => send("Resolved")} className="h-11 rounded-full border-2 border-[#2E9E6B] px-6 font-bold text-[#2E9E6B]" data-testid="complaint-resolve-btn">Mark resolved</button>}
      </div>
    </div>
  );
};

export const AdminSupport = ({ me }) => {
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [selId, setSelId] = useState(null);
  const [f, setF] = useState("open");
  const load = useCallback(() => api.get("/admin/complaints").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/admin/team-lite").then((r) => setTeam(r.data)).catch(() => {}); }, [load]);
  useSync(load);
  const test = { open: (c) => c.status !== "Resolved", mine: (c) => c.assigned_to === me && c.status !== "Resolved", unassigned: (c) => !c.assigned_to && c.status !== "Resolved",
    breached: (c) => sla(c).late, resolved: (c) => c.status === "Resolved" };
  const list = rows.filter(test[f]).sort((a, b) => (sla(b).late - sla(a).late) || (Object.keys(SLA_H).indexOf(a.priority || "Normal") - Object.keys(SLA_H).indexOf(b.priority || "Normal")));
  const sel = rows.find((c) => c.complaint_id === selId);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">{FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} data-testid={`support-filter-${k}`} className={`h-9 rounded-full px-4 text-xs font-bold transition-colors ${f === k ? "bg-[#0D3B2B] text-white" : "border border-[#D6DBD2] bg-white text-[#4A6358]"}`}>
            {l} <span className="opacity-70">{rows.filter(test[k]).length}</span></button>))}</div>
        <ExportButton kind="complaints" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="space-y-2" data-testid="admin-complaints-list">
          {list.map((c) => <Ticket key={c.complaint_id} c={c} team={team} active={selId === c.complaint_id} onClick={() => setSelId(c.complaint_id)} />)}
          {!list.length && <Empty>No tickets in this view.</Empty>}
        </div>
        {sel ? <Detail c={sel} team={team} reload={load} /> : <Empty>Select a ticket to see the conversation.</Empty>}
      </div>
    </div>
  );
};
